"use client"

import { useState } from "react"
import Image from "next/image"
import Link from "next/link"
import { ChevronDown, Menu, X, Phone } from "lucide-react"
import { Button } from "@/components/ui/button"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import SavedProfilesLink from "@/components/saved-profiles-link"

/**
 * Main Navigation Bar - Designer Mockup with Original Site Navigation
 * 
 * Dark gray background with proper navigation structure
 */
export default function Navbar() {
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [expandedMobileItems, setExpandedMobileItems] = useState<string[]>([])
  
  const navigationItems: Array<{ 
    name: string; 
    hasDropdown: boolean; 
    href?: string;
    isHighlighted?: boolean;
    dropdownItems?: Array<{ label: string; href: string }>;
  }> = [
    {
      name: "ROLES",
      hasDropdown: true,
      dropdownItems: [
        { label: "CEO", href: "/role/ceo" },
        { label: "CFO", href: "/role/cfo" },
        { label: "CMO", href: "/role/cmo" },
        { label: "COO", href: "/role/coo" },
        { label: "CCO", href: "/role/cco" },
        { label: "CTO", href: "/role/cto" },
        { label: "CHRO", href: "/role/chro" },
        { label: "General Counsel", href: "/role/general-counsel" },
        { label: "CISO", href: "/role/ciso" },
        { label: "Managing Directors", href: "/managing-directors" },
        { label: "Directors", href: "/directors" },
        { label: "Advisory", href: "/advisory" },
        { label: "Fractional Employment", href: "/fractional-employment" },
        { label: "Funding", href: "/funding" },
        { label: "Digital Marketing", href: "/digital-marketing" },
        { label: "Technical", href: "/technical" },
        { label: "Commercial", href: "/commercial" },
        { label: "Creative", href: "/creative" },
        { label: "Human Resources", href: "/human-resources" },
        { label: "PR", href: "/pr" },
      ]
    },
    {
      name: "SERVICES",
      hasDropdown: true,
      dropdownItems: [
        { label: "Executive Search", href: "/process/executive-search" },
        { label: "Board Assessment", href: "/process/board-assessment" },
        { label: "Leadership Development", href: "/process/leadership-development" },
        { label: "Succession Planning", href: "/process/succession-planning" },
      ]
    },
    {
      name: "SECTORS",
      hasDropdown: true,
      dropdownItems: [
        { label: "Professional Services", href: "/sector/professional-services" },
        { label: "Construction & Property", href: "/sector/construction-property" },
        { label: "International Trade & Investment", href: "/sector/international-trade-investment" },
        { label: "Manufacturing & Engineering", href: "/sector/manufacturing-engineering" },
        { label: "Defence & Security", href: "/sector/defence-security" },
        { label: "Hospitality, Sport, Tourism & Leisure", href: "/sector/hospitality-sport-tourism-leisure" },
        { label: "Media & Entertainment", href: "/sector/media-entertainment" },
        { label: "Medical & Life Sciences", href: "/sector/medical-life-sciences" },
        { label: "Logistics, Haulage & Warehousing", href: "/sector/logistics-haulage-warehousing" },
        { label: "Technology, AI & Telecoms", href: "/sector/technology-ai-telecoms" },
        { label: "Energy, Utilities & Resources", href: "/sector/energy-utilities-resources" },
        { label: "Charity & Third Sector", href: "/sector/charity-third-sector" },
        { label: "Government", href: "/sector/government" },
        { label: "Consumer & Retail", href: "/sector/consumer-retail" },
      ]
    },
    { 
      name: "OUR PROCESS", 
      hasDropdown: false,
      href: "/our-process"
    },
    { 
      name: "ABOUT US", 
      hasDropdown: false,
      href: "/about"
    },
    { 
      name: "CONTACT US", 
      hasDropdown: false,
      href: "/contact-us"
    },
  ]

  return (
    <>
      <nav className="sticky top-0 z-50 bg-[#444446]/95 backdrop-blur-md shadow-sm border-b border-white/5">
        <div className="px-4 sm:px-6 lg:px-8 max-w-[1920px] mx-auto">
          <div className="flex items-center justify-between h-20">
            {/* Logo with SVG */}
            <div className="flex-shrink-0">
              <Link href="/" className="block">
                <Image
                  src="/logo.svg"
                  alt="BoardChampions - Winning Expert Talent"
                  width={200}
                  height={48}
                  className="h-12 w-auto"
                  priority
                />
              </Link>
            </div>

            {/* Desktop Navigation - Center */}
            <div className="hidden lg:flex items-center space-x-1">
              {navigationItems.map((item) => (
                <div key={item.name} className="relative">
                  {item.hasDropdown ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          className="text-white hover:bg-[#6b9bd2] px-5 py-2 h-auto text-sm font-medium flex items-center gap-1 transition-all duration-300 rounded-full focus:bg-[#6b9bd2] focus:outline-none focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
                        >
                          <span>{item.name}</span>
                          <ChevronDown className="h-3 w-3 opacity-60" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent 
                        className="bg-[#555557]/95 backdrop-blur-md border-white/10 shadow-soft-xl rounded-2xl w-64 p-2" 
                        sideOffset={8}
                        align="start"
                      >
                        {item.dropdownItems?.map((dropdownItem) => (
                          <DropdownMenuItem key={dropdownItem.href} asChild className="rounded-xl focus:bg-white/10">
                            <Link
                              href={dropdownItem.href}
                              className="text-white hover:bg-white/10 font-medium px-4 py-2.5 text-sm transition-all duration-200 block rounded-xl"
                            >
                              {dropdownItem.label}
                            </Link>
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : (
                    <Link
                      href={item.href || "#"}
                      className="text-white hover:bg-[#6b9bd2] px-5 py-2 text-sm font-medium transition-all duration-300 inline-flex items-center gap-1 rounded-full focus:bg-[#6b9bd2] focus:outline-none focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
                    >
                      <span>{item.name}</span>
                      <ChevronDown className="h-3 w-3 opacity-60" />
                    </Link>
                  )}
                </div>
              ))}
            </div>

            {/* Right Side - Social Icons and Favourites */}
            <div className="hidden lg:flex items-center space-x-3">
              {/* Social Icons */}
              <div className="flex items-center space-x-2">
                <Link
                  href="https://instagram.com/boardchampions"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Follow us on Instagram"
                  className="w-9 h-9 bg-[#6b9bd2] rounded-full flex items-center justify-center hover:bg-[#5a8bc2] transition-colors"
                >
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073z"/>
                    <path d="M12 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4z"/>
                    <circle cx="18.406" cy="5.594" r="1.44"/>
                  </svg>
                </Link>
                <Link
                  href="https://linkedin.com/company/boardchampions"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Connect with us on LinkedIn"
                  className="w-9 h-9 bg-[#6b9bd2] rounded-full flex items-center justify-center hover:bg-[#5a8bc2] transition-colors"
                >
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                  </svg>
                </Link>
                <Link
                  href="https://twitter.com/boardchampions"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Follow us on X (formerly Twitter)"
                  className="w-9 h-9 bg-[#6b9bd2] rounded-full flex items-center justify-center hover:bg-[#5a8bc2] transition-colors"
                >
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </Link>
                <Link
                  href="https://youtube.com/@boardchampions"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Subscribe to our YouTube channel"
                  className="w-9 h-9 bg-[#6b9bd2] rounded-full flex items-center justify-center hover:bg-[#5a8bc2] transition-colors"
                >
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                </Link>
              </div>

              {/* Saved Profiles Link */}
              <SavedProfilesLink />

              {/* Phone Number */}
              <Link
                href="tel:08453313031"
                className="flex items-center gap-2 text-white hover:text-[#6b9bd2] transition-colors"
              >
                <div className="w-9 h-9 bg-[#6b9bd2] rounded-full flex items-center justify-center">
                  <Phone className="h-4 w-4 text-white" />
                </div>
                <span className="text-sm font-medium hidden xl:block">0845 331 3031</span>
              </Link>
            </div>

            {/* Mobile Menu Toggle */}
            <Button
              variant="ghost"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-white hover:bg-white/10"
              aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? (
                <X className="h-6 w-6" aria-hidden="true" />
              ) : (
                <Menu className="h-6 w-6" aria-hidden="true" />
              )}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-[#555557] border-t border-gray-600 absolute top-full left-0 right-0 shadow-lg z-50 max-h-[calc(100vh-80px)] overflow-y-auto">
            <div className="px-4 py-4">
              {/* Mobile Navigation Items */}
              <div className="space-y-2">
                {navigationItems.map((item) => (
                  <div key={item.name}>
                    {item.hasDropdown ? (
                      <div>
                        <button
                          onClick={() => {
                            setExpandedMobileItems(prev =>
                              prev.includes(item.name)
                                ? prev.filter(i => i !== item.name)
                                : [...prev, item.name]
                            )
                          }}
                          className="w-full flex justify-between items-center px-4 py-3 text-left font-medium transition-colors text-white hover:bg-[#6b9bd2]"
                        >
                          {item.name}
                          <ChevronDown
                            className={`h-4 w-4 transition-transform ${
                              expandedMobileItems.includes(item.name) ? "rotate-180" : ""
                            }`}
                          />
                        </button>
                        {expandedMobileItems.includes(item.name) && (
                          <div className="bg-[#444446] py-2">
                            {item.dropdownItems?.map((subItem) => (
                              <Link
                                key={subItem.href}
                                href={subItem.href}
                                className="block px-8 py-2 text-gray-300 hover:text-white hover:bg-white/10 transition-colors"
                                onClick={() => setIsMobileMenuOpen(false)}
                              >
                                {subItem.label}
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex justify-between items-center px-4 py-3 font-medium transition-colors text-white hover:bg-[#6b9bd2]">
                        <Link
                          href={item.href || "#"}
                          className="flex-1"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          {item.name}
                        </Link>
                        <ChevronDown className="h-4 w-4 opacity-60" />
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Mobile Phone */}
              <div className="mt-6 pt-6 border-t border-gray-600">
                <Link
                  href="tel:08453313031"
                  className="w-full bg-[#6b9bd2] hover:bg-[#5a8bc2] text-white font-medium flex items-center justify-center gap-2 py-3 rounded-md transition-colors"
                >
                  <Phone className="h-5 w-5" />
                  Call: 0845 331 3031
                </Link>
              </div>

              {/* Mobile Social Icons */}
              <div className="mt-6 pt-6 border-t border-gray-600">
                <div className="flex justify-center space-x-3">
                  <Link
                    href="https://instagram.com/boardchampions"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Follow us on Instagram"
                    className="w-10 h-10 bg-[#6b9bd2] rounded-full flex items-center justify-center"
                  >
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073z"/>
                      <path d="M12 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4z"/>
                      <circle cx="18.406" cy="5.594" r="1.44"/>
                    </svg>
                  </Link>
                  <Link
                    href="https://linkedin.com/company/boardchampions"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Connect with us on LinkedIn"
                    className="w-10 h-10 bg-[#6b9bd2] rounded-full flex items-center justify-center"
                  >
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                    </svg>
                  </Link>
                  <Link
                    href="https://twitter.com/boardchampions"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Follow us on X (formerly Twitter)"
                    className="w-10 h-10 bg-[#6b9bd2] rounded-full flex items-center justify-center"
                  >
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                  </Link>
                  <Link
                    href="https://youtube.com/@boardchampions"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Subscribe to our YouTube channel"
                    className="w-10 h-10 bg-[#6b9bd2] rounded-full flex items-center justify-center"
                  >
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                    </svg>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}
      </nav>
    </>
  )
}